import { ClientEndpointType, WebRelayClientEndpoint, WebRelayModel } from "@app/shared";
import { useModel } from "@app/shared/src/hooks";
import { EnumSelect, Input, InputRow } from "@app/dashboard/src/components/shared/inputs";
import { ClientEndpointEditorProps } from "./ClientEndpointEditor";
import { WEB_RELAY_AUTO_DETECT_FIRMWARE_VERSION } from "@app/dashboard/src/helpers/versionCompare";

type EditWebRelayClientEndpointProps = {
} & ClientEndpointEditorProps;

// The enum names are the firmware's; the labels say which physical board each one is, in the words on
// the vendor's product page, because "Quad_Original" vs "Quad_400_Series" is exactly the choice people
// got wrong. Auto is listed first: it is what the work-zone console provisions, and the right answer for
// anyone who does not know which generation of board is in the truck.
const relayModelLabels: Record<keyof typeof WebRelayModel, string> = {
    Auto: "Auto — detect on device",
    Single: "Single (original WebRelay)",
    Quad_Original: "Quad (original WebRelay-Quad)",
    Single_400_Series: "Single (400 series)",
    Quad_400_Series: "Quad (400 series)",
};

export const EditWebRelayClientEndpoint: React.FC<EditWebRelayClientEndpointProps> = (props) => {
    const [model, helper] = useModel(WebRelayClientEndpoint, props.endpoint as WebRelayClientEndpoint);
    const onChange = (newEndpoint: Partial<WebRelayClientEndpoint>) => {
        props.onChange({ ...newEndpoint as WebRelayClientEndpoint, endpointType: ClientEndpointType.WebRelay });
    };
    return (
        <>
            <InputRow><Input label="Endpoint Name" {...helper.bindingsFor("endpointName", onChange)} /></InputRow>
            <InputRow>
                <EnumSelect
                    label="Relay Model"
                    enumType={WebRelayModel}
                    nameToDisplayConverter={(key) => relayModelLabels[key] ?? key}
                    {...helper.bindingsFor("model", onChange)}
                />
            </InputRow>
            <InputRow>
                <p className="text-xs text-muted-body-text -mt-3">
                    {model.model === WebRelayModel.Auto
                        ? `The device reads the board's state.xml to work out which model it is, and keeps trying until it gets an answer. Units on firmware below ${WEB_RELAY_AUTO_DETECT_FIRMWARE_VERSION} cannot detect reliably and are sent Quad (400 series) instead.`
                        : "The device checks the board's state.xml against this model."}
                    {" "}A board that can't be reached, or that answers as a different model, raises a health alert on the device.
                </p>
            </InputRow>
            <InputRow><Input label="IP Address" {...helper.bindingsFor("ipAddress", onChange)} /></InputRow>
            <InputRow><Input label="Control Password" {...helper.bindingsFor("password", onChange)} /></InputRow>
        </>
    );
}
